import React, {useState} from 'react';
import {Avatar, Box, Chip, FormControl, InputLabel, MenuItem, Select, Stack} from '@mui/material';
import {type GridColDef, type GridEventListener} from '@mui/x-data-grid';
import {useNavigate} from 'react-router-dom';

import {getName, shortener} from '@/global/helpers/text';
import {apiSchoolTickets} from "@/modules/tickets/services/tickets.service";
import type {TicketDto, TicketDtoStatusEnum} from "@/global/api/gen/ts/TicketDto";
import {ServerDataGrid} from "@/global/components/ServerDataGrid";
import {TicketStatus, TicketsTypes} from "@/modules/tickets";
import {AutocompleteSchool} from "@/global/components/AutocompleteSchool";

interface Props {
    school_id?: number,
}

export function SchoolTicketsTable({school_id}: Props) {

    const navigate = useNavigate();

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 25, page: 0
    });

    const [schoolFilter, setSchoolFilter] = useState<number | undefined | null>(school_id);
    const [statusFilter, setStatusFilter] = useState<TicketDtoStatusEnum | undefined>(undefined);
    const [titleFilter, setTitleFilter] = useState<string | undefined>(undefined);

    const {data, isLoading} = apiSchoolTickets({
        offset: paginationModel.page * paginationModel.pageSize,
        limit: paginationModel.pageSize,
        status: statusFilter,
        title: titleFilter,
        school_id: schoolFilter,
    });

    const columns: GridColDef<TicketDto>[] = [
        {
            field: 'id',
            headerName: 'ID',
            width: 150,
            sortable: false,
        },
        {
            field: 'title',
            headerName: 'Тема обращения',
            width: 300,
            sortable: false,
            renderCell: ({row}) => TicketsTypes[row.title as keyof typeof TicketsTypes]
        },
        {
            field: 'user',
            headerName: 'Пользователь',
            width: 300,
            sortable: false,
            renderCell: ({row}) => {
                const name = getName(row.user ?? {});
                return (
                    <React.Fragment>
                        <Avatar
                            sx={{mr: 1.5, fontSize: 16, color: '#99999F', bgcolor: '#D9E8FF'}}
                        >
                            {name.partialName}
                        </Avatar>
                        {name.fullName}
                    </React.Fragment>
                );
            }
        },
        {
            field: 'status',
            headerName: 'Статус',
            width: 200,
            sortable: false,
            renderCell: ({row}) => {
                const element = TicketStatus[row.status];
                return (
                    <Chip
                        sx={{
                            backgroundColor: element.color,
                        }}
                        label={element.label}
                    />
                );
            }
        },
        {
            field: 'messages',
            headerName: 'Сообщение',
            width: 200,
            sortable: false,
            renderCell: ({row}) => {
                return row.messages.length ? shortener(row.messages.at(-1)!.message, 20) : 'Нет сообщений';
            }
        }
    ];

    const handleRowClick: GridEventListener<'rowClick'> = (
        params,
    ) => {
        navigate('/tickets/' + params.row.id);
    };

    return (
        <Box
            className={'appeals'}
        >
            {
                !school_id && (
                    <Stack
                        flexDirection={'row'}
                        flexWrap={'wrap'}
                        gap={2}
                        sx={{
                            marginY: 2,
                        }}
                    >
                        <FormControl
                            sx={{
                                minWidth: '300px'
                            }}
                        >
                            <InputLabel>
                                Выбор статуса
                            </InputLabel>
                            <Select
                                value={statusFilter}
                                label={'Выбор статуса'}
                                onChange={(event) => setStatusFilter(event.target.value as TicketDtoStatusEnum)}
                                sx={{
                                    '&.MuiOutlinedInput-root': {
                                        border: 'none',
                                        minWidth: '300px',
                                    }
                                }}
                            >
                                <MenuItem value={undefined}>
                                    Не выбрано
                                </MenuItem>
                                {Object.entries(TicketStatus).map(([value, title]) => (
                                    <MenuItem
                                        key={value}
                                        value={value}
                                    >
                                        {title.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl
                            sx={{
                                minWidth: '300px'
                            }}
                        >
                            <InputLabel>
                                Выбор темы
                            </InputLabel>
                            <Select
                                value={titleFilter}
                                label={'Выбор темы'}
                                onChange={(event) => setTitleFilter(event.target.value)}
                                sx={{
                                    '&.MuiOutlinedInput-root': {
                                        border: 'none',
                                        minWidth: '300px',
                                    }
                                }}
                            >
                                <MenuItem value={undefined}>
                                    Не выбрано
                                </MenuItem>
                                {Object.entries(TicketsTypes).map(([title, description]) => (
                                    <MenuItem
                                        key={title}
                                        value={title}
                                    >
                                        {description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box
                            sx={{
                                width: '300px',
                            }}
                        >
                            <AutocompleteSchool onChange={(value) => setSchoolFilter(value)}/>
                        </Box>
                    </Stack>
                )}
            <ServerDataGrid
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                className={'appeals-grid'}
                rows={data?.items ?? []}
                onRowClick={handleRowClick}
                columns={columns}
                loading={isLoading}
                rowCount={data?.total ?? 0}
                autoHeight={true}
                disableRowSelectionOnClick
            />
        </Box>
    );
}